import type {
  OrgSecretScope,
  OrgSecretScopeInput,
  ScopeProject,
} from './maestro-org-api';

/** One select value per scope; the project id travels with an application or agent. */
export type ScopeTarget =
  | 'org'
  | `project:${number}`
  | `application:${number}:${number}`
  | `agent:${number}:${number}`;

export interface ScopeOption {
  value: ScopeTarget;
  kind: OrgSecretScope['type'];
  name: string;
  project: string | null;
}

export function scopeTarget(scope: OrgSecretScope): ScopeTarget {
  const projectId = scope.project?.id;
  if (projectId === undefined) return 'org';
  if (scope.type === 'agent' && scope.agent)
    return `agent:${projectId}:${scope.agent.id}`;
  if (scope.type === 'application' && scope.application)
    return `application:${projectId}:${scope.application.id}`;
  return `project:${projectId}`;
}

/** The mutation input for a target. An agent carries its application, as the backend allows. */
export function scopeInput(
  target: ScopeTarget,
  projects: readonly ScopeProject[]
): OrgSecretScopeInput {
  const [type, rawProject, rawId] = target.split(':');
  const projectId = Number(rawProject);
  const id = Number(rawId);
  if (type === 'project') return { scopeType: 'project', projectId };
  if (type === 'application')
    return { scopeType: 'application', projectId, applicationId: id };
  if (type === 'agent') {
    const applicationId = projects
      .find((p) => p.id === projectId)
      ?.agents.find((a) => a.id === id)?.applicationId;
    return applicationId == null
      ? { scopeType: 'agent', projectId, agentId: id }
      : { scopeType: 'agent', projectId, applicationId, agentId: id };
  }
  return { scopeType: 'org' };
}

export function scopeOptions(projects: readonly ScopeProject[]): ScopeOption[] {
  const out: ScopeOption[] = [
    { value: 'org', kind: 'org', name: '', project: null },
  ];
  [...projects]
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((project) => {
      out.push({
        value: `project:${project.id}`,
        kind: 'project',
        name: project.name,
        project: null,
      });
      project.applications.forEach((app) =>
        out.push({
          value: `application:${project.id}:${app.id}`,
          kind: 'application',
          name: app.name,
          project: project.name,
        })
      );
      project.agents.forEach((agent) =>
        out.push({
          value: `agent:${project.id}:${agent.id}`,
          kind: 'agent',
          name: agent.name,
          project: project.name,
        })
      );
    });
  return out;
}

export const targetProject = (target: ScopeTarget): number | null =>
  target === 'org' ? null : Number(target.split(':')[1]);

/** maestro refuses a move between two projects, so a move stays in its project or goes to or from Organization. */
export const moveOptions = (
  options: readonly ScopeOption[],
  from: ScopeTarget
): ScopeOption[] => {
  const project = targetProject(from);
  return options.filter((o) => {
    const at = targetProject(o.value);
    return project === null || at === null || at === project;
  });
};

export const isScopeTarget = (
  value: string,
  options: readonly ScopeOption[]
): value is ScopeTarget => options.some((o) => o.value === value);
